import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Link2Off, PlusCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../../lib/auth/auth-context.js';
import {
  useAttendanceKioskLink,
  useRevokeAttendanceKioskLink,
  useRotateAttendanceKioskLink,
} from '../../lib/attendance/use-attendance.js';
import { Button, useConfirm, useToast } from '@tailoring-crm/ui';

/**
 * The owner's end of the attendance kiosk link: a credential shown in full, because a shop owner
 * has to be able to read it aloud, with two controls that both really do mean it. Rotating
 * overwrites the token server-side, so the confirmation says, in words, that everyone still
 * holding the old link is shut out immediately — there is no grace period to soften that warning
 * with.
 *
 * `selfCheckInEnabled` is read off the same response rather than a second query, so the card can
 * explain in one place why a perfectly valid link might still be refused at the door — the API
 * checks that setting before it ever looks up an identity, and this card is where a shop learns
 * that ordering matters.
 */
export function AttendanceKioskLinkCard(): ReactElement | null {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();
  const confirm = useConfirm();

  const link = useAttendanceKioskLink({ enabled: role !== 'tailor' });
  const rotate = useRotateAttendanceKioskLink();
  const revoke = useRevokeAttendanceKioskLink();

  /**
   * Mirrors the `attendance.settings.write` row of the permission matrix — the same permission
   * that already guards writing attendance settings, reused rather than given a new row. The
   * API is the authority and refuses a tailor whatever this renders; hiding the control here
   * only spares them a button that would 403.
   */
  if (role === 'tailor') return null;
  if (!link.data) return null;

  const { token, url, revokedAt, selfCheckInEnabled } = link.data;

  const report = (error: unknown): void => {
    toast({
      title: error instanceof Error ? error.message : t('error.unexpected'),
      variant: 'destructive',
    });
  };

  const onCopy = async (): Promise<void> => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: t('attendance.kiosk.copied'), variant: 'success' });
    } catch {
      // Clipboard access is denied on an insecure origin and in some in-app browsers. The
      // link is on screen and selectable, so this is a nudge rather than a failure.
      toast({ title: t('attendance.kiosk.copyFailed') });
    }
  };

  const onRotate = async (): Promise<void> => {
    const ok = await confirm({
      title: t('attendance.kiosk.rotateTitle'),
      description: t('attendance.kiosk.rotateWarning'),
      confirmLabel: t('attendance.kiosk.rotateConfirm'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await rotate.mutateAsync();
      toast({ title: t('attendance.kiosk.rotated'), variant: 'success' });
    } catch (error) {
      report(error);
    }
  };

  const onCreate = async (): Promise<void> => {
    try {
      await rotate.mutateAsync();
      toast({ title: t('attendance.kiosk.rotated'), variant: 'success' });
    } catch (error) {
      report(error);
    }
  };

  const onRevoke = async (): Promise<void> => {
    const ok = await confirm({
      title: t('attendance.kiosk.revokeTitle'),
      description: t('attendance.kiosk.revokeWarning'),
      confirmLabel: t('attendance.kiosk.revokeConfirm'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await revoke.mutateAsync();
      toast({ title: t('attendance.kiosk.revoked'), variant: 'success' });
    } catch (error) {
      report(error);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <div>
        <h3 className="text-sm font-semibold">{t('attendance.kiosk.heading')}</h3>
        <p className="text-sm text-muted-foreground">
          {revokedAt ? t('attendance.kiosk.revokedHint') : t('attendance.kiosk.hint')}
        </p>
        <p className="text-sm text-muted-foreground">
          {selfCheckInEnabled
            ? t('attendance.kiosk.selfCheckInOn')
            : t('attendance.kiosk.selfCheckInOff')}
        </p>
      </div>

      {token && url ? (
        <p className="break-all rounded-md bg-muted/50 p-2 font-mono text-xs">{url}</p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {token && url ? (
          <>
            <Button variant="outline" size="sm" onClick={() => void onCopy()}>
              <Copy aria-hidden="true" />
              {t('attendance.kiosk.copy')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={rotate.isPending}
              onClick={() => void onRotate()}
            >
              <RefreshCw aria-hidden="true" />
              {t('attendance.kiosk.rotate')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => void onRevoke()}
            >
              <Link2Off aria-hidden="true" />
              {t('attendance.kiosk.revoke')}
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={rotate.isPending}
            onClick={() => void onCreate()}
          >
            <PlusCircle aria-hidden="true" />
            {t('attendance.kiosk.create')}
          </Button>
        )}
      </div>
    </section>
  );
}

export default AttendanceKioskLinkCard;
